import { ClientError, ClientErrorCode } from '~/shared';
import { EventBus } from '~/rage/common';
import { IRpcService } from './rpcServiceTypes';

type RpcCallback<T = unknown> = (payload: T) => unknown | Promise<unknown>;
type RpcRegistry = Record<string, RpcCallback<unknown>>;

declare global {
	interface Window {
		__rpc?: RpcRegistry;
	}
}

type RpcRequest = {
	id: number;
	name: string;
	payload?: unknown;
};

type RpcResponse =
	| { id: number; success: true; data?: unknown }
	| {
			id: number;
			success: false;
			error: {
				code: ClientErrorCode;
				params?: Record<string, unknown> | null;
				message?: string;
			};
	  };

type PendingCall = {
	resolve: (value: unknown) => void;
	reject: (error: Error) => void;
	timeout: ReturnType<typeof setTimeout>;
};

export class RpcService implements IRpcService {
	private readonly timeoutMs: number;
	private readonly maxPayloadLength: number;

	private callId = 0;
	private pendingCalls = new Map<number, PendingCall>();

	constructor(options: { timeoutMs?: number; maxPayloadLength?: number } = {}) {
		this.timeoutMs = options.timeoutMs ?? 10_000;
		this.maxPayloadLength = options.maxPayloadLength ?? 512_000;

		this.register('_rpc:response', this.handleResponse.bind(this));
	}

	/**
	 * Вызов RPC на клиенте
	 */
	public async invokeClient<T>(
		methodName: string,
		payload?: unknown,
	): Promise<T> {
		const id = ++this.callId;
		const requestJson = this.stringifyRequest(
			{ id, name: methodName, payload },
			methodName,
		);

		return new Promise<T>((resolve, reject) => {
			const timeout = setTimeout(() => {
				this.pendingCalls.delete(id);
				reject(this.createInternalError(methodName, 'timeout'));
			}, this.timeoutMs);

			this.pendingCalls.set(id, {
				resolve: resolve as (value: unknown) => void,
				reject,
				timeout,
			});

			EventBus.triggerClient('_rpc:gui:request', requestJson);
		});
	}

	/**
	 * Вызов RPC на сервере
	 */
	public async invokeServer<T>(
		methodName: string,
		payload?: unknown,
	): Promise<T> {
		console.log('[RPC-server] ' + methodName, payload);
		return this.invokeClient('rpc:callServer', {
			method: methodName,
			payload,
		});
	}

	public register<T>(methodName: string, callback: (payload: T) => void): void {
		if (!window.__rpc) {
			window.__rpc = {};
		}

		window.__rpc[methodName] = callback as RpcCallback<unknown>;
	}

	public async callLocal(
		_methodName: string,
		_payload?: unknown,
	): Promise<void> {
		throw new Error('Local RPC call disabled for non-stuf environment');
	}

	private handleResponse(response: RpcResponse): void {
		const call = this.pendingCalls.get(response.id);
		if (!call) {
			return;
		}

		clearTimeout(call.timeout);
		this.pendingCalls.delete(response.id);

		if (response.success) {
			call.resolve(response.data);
			return;
		}

		call.reject(
			new ClientError(response.error.code, response.error.params ?? null),
		);
	}

	private stringifyRequest(request: RpcRequest, methodName: string): string {
		const json = JSON.stringify(request);
		if (json.length > this.maxPayloadLength) {
			throw this.createInternalError(methodName, 'payload_too_large');
		}

		return json;
	}

	private createInternalError(methodName: string, reason: string): ClientError {
		return new ClientError(ClientErrorCode.InternalError, {
			method: methodName,
			reason,
		});
	}
}
